import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import {
  PROPOSAL_BUNDLE_STATE,
  hasProposalItemHotel,
  type HotelProposalBundle,
  type HotelProposalItem,
  type ProposalBundleState
} from '../../types/proposalBundles';
import { formatRouteDate } from '../../utils/routeInfoFormatter';
import { formatPriceWithMark } from '@this/src/util/price/price.util';

interface Props {
  bundle: HotelProposalBundle;
  includeTax?: boolean;
}

type HotelSelectedItemSummary = {
  id: number;
  title: string;
  price: number | null;
  priceWithTax: number | null;
  segmentPeriod: string | null;
};

type HotelBundleSummaryView = {
  routeInfo: string | null;
  dateInfoText: string | null;
  stayDaysText: string | null;
  partitionProgressText: string | null;
  selectedItems: HotelSelectedItemSummary[];
  totalPrice: { price: number; priceWithTax: number } | null;
};

const isPartitionedHotelBundle = (bundle: HotelProposalBundle): boolean =>
  bundle.partition.partitioned === true && (bundle.partition.segmentCount ?? 0) > 1;

const getSelectedHotelItems = (bundle: HotelProposalBundle): HotelProposalItem[] => {
  if (isPartitionedHotelBundle(bundle)) {
    const segmentMap = new Map<number, HotelProposalItem[]>();
    bundle.items.filter(hasProposalItemHotel).forEach(item => {
      const segmentIndex = item.hotel.segment.index ?? 0;
      const existing = segmentMap.get(segmentIndex);
      if (existing) {
        existing.push(item);
        return;
      }
      segmentMap.set(segmentIndex, [item]);
    });

    return Array.from(segmentMap.values())
      .map(items => items.find(item => item.selected))
      .filter((item): item is HotelProposalItem => item != null && hasProposalItemHotel(item));
  }

  const selectedItem = bundle.items.find(
    (item): item is HotelProposalItem => item.selected && hasProposalItemHotel(item)
  );
  return selectedItem ? [selectedItem] : [];
};

const calculateHotelItemTotal = (
  item: HotelProposalItem,
  fallbackStayDays: number | null = null
): { price: number | null; priceWithTax: number | null } => {
  const stayDays =
    item.hotel.stayDays && item.hotel.stayDays > 0
      ? item.hotel.stayDays
      : fallbackStayDays && fallbackStayDays > 0
      ? fallbackStayDays
      : 1;

  return {
    price: item.price !== null ? item.price * stayDays : null,
    priceWithTax: item.priceWithTax !== null ? item.priceWithTax * stayDays : null
  };
};

const sumSelectedHotelItemsTotal = (selectedItems: HotelProposalItem[]): { price: number; priceWithTax: number } =>
  selectedItems.reduce(
    (acc, item) => {
      const totals = calculateHotelItemTotal(item);
      return {
        price: acc.price + (totals.price ?? 0),
        priceWithTax: acc.priceWithTax + (totals.priceWithTax ?? 0)
      };
    },
    { price: 0, priceWithTax: 0 }
  );

const formatSegmentPeriod = (checkin: string | null, checkout: string | null): string | null => {
  const checkinLabel = formatRouteDate(checkin);
  const checkoutLabel = formatRouteDate(checkout);
  if (checkinLabel && checkoutLabel) {
    return `${checkinLabel}〜${checkoutLabel}`;
  }
  return checkinLabel ?? checkoutLabel;
};

const buildHotelBundleSummary = (bundle: HotelProposalBundle): HotelBundleSummaryView => {
  const checkinStr = formatRouteDate(bundle.route.departureDate);
  const checkoutStr = formatRouteDate(bundle.route.returnDate);
  const dateInfoText = (() => {
    if (checkinStr && checkoutStr) return `${checkinStr}〜${checkoutStr}`;
    if (checkinStr) return checkinStr;
    return null;
  })();

  const stayDays = bundle.route.stayDays ?? null;
  const isPartitioned = isPartitionedHotelBundle(bundle);
  const stayDaysText = stayDays && stayDays > 0 && !isPartitioned ? `${stayDays}泊${stayDays + 1}日` : null;

  const routeInfo =
    bundle.route.origin && bundle.route.destination
      ? `${bundle.route.origin} → ${bundle.route.destination}`
      : null;

  const selectedHotelItems = getSelectedHotelItems(bundle);
  const selectedItems = selectedHotelItems.map(item => {
    const totals = calculateHotelItemTotal(item, bundle.route.stayDays ?? null);
    const segmentPeriod =
      isPartitioned && item.hotel.segment.checkin
        ? formatSegmentPeriod(item.hotel.segment.checkin, item.hotel.segment.checkout)
        : null;

    return {
      id: item.id,
      title: item.title || '-',
      price: totals.price,
      priceWithTax: totals.priceWithTax,
      segmentPeriod
    };
  });

  const totalPrice =
    isPartitioned && selectedItems.length > 0 ? sumSelectedHotelItemsTotal(selectedHotelItems) : null;

  const segmentCount = bundle.partition.segmentCount ?? 0;
  const partitionProgressText =
    isPartitioned && segmentCount > 1
      ? `${segmentCount}つの期間に分割（${selectedItems.length}/${segmentCount} 選択済）`
      : null;

  return {
    routeInfo,
    dateInfoText,
    stayDaysText,
    partitionProgressText,
    selectedItems,
    totalPrice
  };
};

const getStateLabel = (state: ProposalBundleState): { text: string; color: string } => {
  switch (state) {
    case PROPOSAL_BUNDLE_STATE.SUGGESTING:
      return { text: '提案中', color: '#d4a60d' };
    case PROPOSAL_BUNDLE_STATE.SELECTED:
      return { text: '選択済み', color: '#4a9464' };
    case PROPOSAL_BUNDLE_STATE.REJECTED:
      return { text: '予約しない', color: '#888888' };
    default:
      return { text: state, color: '#666' };
  }
};

const HotelLatestProposalSummary = ({ bundle, includeTax = false }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const summary = useMemo(() => buildHotelBundleSummary(bundle), [bundle]);
  const stateInfo = getStateLabel(bundle.state);

  return (
    <div style={styles.bundleCard}>
      <div style={styles.bundleHeader}>
        <span style={styles.categoryLabel}>ホテル</span>
        <span style={{ ...styles.stateLabel, color: stateInfo.color }}>{stateInfo.text}</span>
      </div>
      {(summary.routeInfo || summary.dateInfoText) && (
        <div style={styles.routeInfo}>
          {summary.routeInfo && <span>{summary.routeInfo}</span>}
          {summary.dateInfoText && <span style={styles.dateText}>（{summary.dateInfoText}）</span>}
          {summary.stayDaysText && <span style={styles.dateText}> / {summary.stayDaysText}</span>}
        </div>
      )}
      {summary.partitionProgressText && (
        <div style={styles.partitionProgress}>{summary.partitionProgressText}</div>
      )}
      {summary.selectedItems.map(item => (
        <div key={item.id} style={styles.selectedItemInfo}>
          {item.segmentPeriod && <div style={styles.segmentPeriodLabel}>期間: {item.segmentPeriod}</div>}
          <div style={styles.selectedItemTitle}>{item.title}</div>
          <div style={styles.selectedItemPrice}>
            {formatPriceWithMark(includeTax ? item.priceWithTax : item.price) ?? '-'}
          </div>
        </div>
      ))}
      {summary.totalPrice && (
        <div style={styles.totalRow}>
          <span style={styles.totalLabel}>宿泊合計</span>
          <span style={styles.totalPrice}>
            {formatPriceWithMark(includeTax ? summary.totalPrice.priceWithTax : summary.totalPrice.price) ?? '-'}
          </span>
        </div>
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  bundleCard: {
    padding: '10px 12px',
    backgroundColor: theme.contentBgColor,
    borderRadius: '6px',
    border: `1px solid ${theme.grayBorderColor}`
  },
  bundleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px'
  },
  categoryLabel: {
    fontSize: '13px',
    fontWeight: 600 as const,
    flex: 1
  },
  stateLabel: {
    fontSize: '11px',
    fontWeight: 500 as const
  },
  routeInfo: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginBottom: '4px'
  },
  dateText: {
    color: theme.grayTextColor
  },
  partitionProgress: {
    fontSize: '11px',
    color: theme.grayTextColor,
    marginBottom: '4px'
  },
  selectedItemInfo: {
    marginTop: '6px',
    padding: '8px',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px'
  },
  segmentPeriodLabel: {
    fontSize: '11px',
    color: theme.grayTextColor,
    marginBottom: '2px'
  },
  selectedItemTitle: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  selectedItemPrice: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    marginTop: '4px'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: `1px solid ${theme.grayBorderColor}`
  },
  totalLabel: {
    fontSize: '12px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  totalPrice: {
    fontSize: '14px',
    fontWeight: 700 as const,
    color: theme.linkColor
  }
});

export default HotelLatestProposalSummary;
